import { ApolloError, useMutation } from '@apollo/client'

import { LocalFile, MutationUpdateObservationArgs, Observation, ObservationInput, RemoteFile, Scalars } from '@types'

import { MUTATION_UPDATE_OBSERVATION } from '@libs/apollo/mutations/observation.mutation'
import { useCreateFile } from '../file/create-file.hook'

type ObservationInputWithoutFiles = Omit<ObservationInput, 'fileIds'>

type ObservationCreateInputWithoutFiles = {
  observation: ObservationInputWithoutFiles
  observationId: Scalars['ID']['input']
}

interface Data {
  updateObservation: Observation
}

interface UseUpdateObservationProps {
  onCompleted?: () => void
}

export const useUpdateObservation = (
  props?: UseUpdateObservationProps,
): {
  updateObservation: (input: ObservationCreateInputWithoutFiles, files: (LocalFile | RemoteFile)[]) => void
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const { createFile } = useCreateFile()

  const [updateObservation, { loading, error, data }] = useMutation<Data, MutationUpdateObservationArgs>(
    MUTATION_UPDATE_OBSERVATION,
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const _updateObservation = async (input: ObservationCreateInputWithoutFiles, files: (LocalFile | RemoteFile)[]) => {
    const localFiles = files.filter((file): file is LocalFile => Boolean((file as LocalFile).data))
    const remoteFiles = files
      .filter((file): file is RemoteFile => !Boolean((file as LocalFile).data))
      .filter((file) => !file.deleted)

    const fileIds = remoteFiles.map((file) => file.id)

    for (const file of localFiles) {
      const remoteFile = await createFile(file)
      remoteFile?.id && fileIds.push(remoteFile.id)
    }

    const { observationId, observation } = input

    updateObservation({
      variables: {
        input: {
          observationId,
          observation: {
            ...observation,
            fileIds,
          },
        },
      },
      onCompleted: props?.onCompleted,
    })
  }

  return { updateObservation: _updateObservation, loading, error, data }
}
